import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
// `Map` is aliased: the un-aliased import shadowed the JavaScript built-in, so
// `new Map()` below tried to construct a React component and took the page down
// with "Map is not a constructor" (2026-07-27).
import { APIProvider, Map as GoogleMap, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';

/**
 * Coordinates are not an answer to "from where".
 *
 * The owner asked for the place a driver clocked on from, and 25.0764, 55.1329 is not
 * a place. The Maps JS API is already loaded for the map beside this panel, so
 * its geocoder costs no new key and no new script — it just needs asking.
 *
 * Cached across the whole page: a week of history re-asks for the same depot
 * every single day otherwise. Falls back to the coordinates, which are at least
 * true, and never blocks the row from rendering.
 */
const placeCache = new Map();

function PlaceName({ lat, lng }) {
    const key = `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;
    const [name, setName] = useState(() => placeCache.get(key) || '');

    useEffect(() => {
        if (!lat || !lng || placeCache.has(key)) return;
        const g = window.google?.maps;
        if (!g?.Geocoder) return;
        let alive = true;
        new g.Geocoder()
            .geocode({ location: { lat: Number(lat), lng: Number(lng) } })
            .then((r) => {
                const best = r?.results?.[0];
                if (!best) return;
                /* The neighbourhood reads better than a full postal address. */
                const parts = best.address_components || [];
                const pick = (t) => parts.find((c) => c.types.includes(t))?.long_name;
                const label = pick('neighborhood') || pick('sublocality')
                    || pick('locality') || best.formatted_address;
                placeCache.set(key, label);
                if (alive) setName(label);
            })
            .catch(() => {});
        return () => { alive = false; };
    }, [lat, lng, key]);

    if (!lat || !lng) return <em className="dt-noplace">location not recorded</em>;
    return <>{name || key}</>;
}

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY || '';
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const DUBAI = { lat: 25.2048, lng: 55.2708 };

/**
 * Where the staff are, and where they have been. Spec: PLAN_driver_routing.md
 * (phase 5).
 *
 * LAYOUT: the map gets the room, and everything you operate — who, which day,
 * which hours, and the list itself — lives in one column on the RIGHT
 * (2026-07-27). Controls strung across the top pushed the map down and made
 * the eye travel between opposite corners to answer a single question.
 *
 * Honest about staleness: a pin is "last seen", never "is". A phone that is
 * off, force-quit or out of signal simply stops reporting, so every pin and row
 * carries its age rather than implying the car is there now.
 */

/* ─────────────────────────────── helpers ─────────────────────────────── */

const ROLE_TONE = {
    driver: { bg: '#4c7ac5', label: 'Driver', glyph: '🚐' },
    cleaner: { bg: '#a43777', label: 'Cleaner', glyph: '🧹' },
    'part-time cleaner': { bg: '#a43777', label: 'Cleaner', glyph: '🧹' },
};
const toneFor = (role) => ROLE_TONE[role] || { bg: '#6c757d', label: role || 'Staff', glyph: '📍' };

const initials = (name) => (name || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

const fullName = (p) => [p.first_name, p.last_name].filter(Boolean).join(' ')
    || `Staff #${p.user_id}`;

const sinceLabel = (iso) => {
    if (!iso) return 'never';
    const mins = Math.max(0, Math.round(
        DateTime.now().diff(DateTime.fromISO(iso), 'minutes').minutes
    ));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m ago`;
};

/** Fresh, going stale, or long gone — drives the colour of the age. */
const freshness = (iso) => {
    const mins = DateTime.now().diff(DateTime.fromISO(iso), 'minutes').minutes;
    if (mins <= 10) return 'live';
    if (mins <= 60) return 'recent';
    return 'stale';
};

const hhmm = (iso) => DateTime.fromISO(iso).setZone('Asia/Dubai').toFormat('HH:mm');

/**
 * One line of English per marked stop. No cleaner = a courier leg, labelled
 * with the booking's own type name (Delivery Service Only etc.), never an
 * invented word (2026-08-09).
 */
const stopLabel = (st) => {
    const skip = st.status === 'skipped' ? 'Skipped — ' : '';
    if (st.stop_type === 'pickup') return skip + (st.cleaner ? `Picked up ${st.cleaner} from home` : (st.booking_type || 'Pickup'));
    if (st.stop_type === 'dropoff') return skip + (st.cleaner ? `Dropped ${st.cleaner}` : (st.booking_type || 'Delivery'));
    if (st.stop_type === 'collect') return skip + (st.cleaner ? `Collected ${st.cleaner}` : (st.booking_type || 'Pickup'));
    if (st.stop_type === 'home') return skip + (st.cleaner ? `Dropped ${st.cleaner} home` : 'Returned to base');
    return skip + st.stop_type;
};

/* ──────────────────────────── map behaviours ──────────────────────────── */

/** Frame what is shown, so a pin outside Dubai is never invisible. */
function FitToPoints({ points, paused }) {
    const map = useMap();
    useEffect(() => {
        // While a ledger row's point is focused, the fit must not fight the
        // pan — mapPoints is rebuilt every render, so this effect would
        // otherwise re-frame the day and snap the map straight back.
        if (paused) return;
        if (!map || !window.google || !points.length) return;
        if (points.length === 1) {
            map.setCenter({ lat: Number(points[0].lat), lng: Number(points[0].lng) });
            map.setZoom(15);
            return;
        }
        const bounds = new window.google.maps.LatLngBounds();
        points.forEach((p) => bounds.extend({ lat: Number(p.lat), lng: Number(p.lng) }));
        map.fitBounds(bounds, 72);
    }, [map, points, paused]);
    return null;
}

/**
 * Pan the map to a ledger row's point when clicked (2026-08-09).
 */
function PanToFocus({ point }) {
    const map = useMap();
    useEffect(() => {
        if (!map || !point) return;
        map.panTo({ lat: Number(point.lat), lng: Number(point.lng) });
        if (map.getZoom() < 15) map.setZoom(15);
    }, [map, point]);
    return null;
}

/** The day as a line, with a white casing so it reads over any basemap. */
function Trail({ points }) {
    const map = useMap();
    useEffect(() => {
        if (!map || !window.google || points.length < 2) return undefined;
        const path = points.map((p) => ({ lat: Number(p.lat), lng: Number(p.lng) }));
        const casing = new window.google.maps.Polyline({
            path, strokeColor: '#ffffff', strokeOpacity: 0.95, strokeWeight: 8, zIndex: 1,
        });
        const line = new window.google.maps.Polyline({
            path, strokeColor: '#FF5A60', strokeOpacity: 1, strokeWeight: 4, zIndex: 2,
        });
        casing.setMap(map);
        line.setMap(map);
        return () => { casing.setMap(null); line.setMap(null); };
    }, [map, points]);
    return null;
}

/* ────────────────────────────── component ────────────────────────────── */

export default function DriverTracking({ drivers }) {
    const [mode, setMode] = useState('live');
    const [positions, setPositions] = useState([]);
    const [staff, setStaff] = useState([]);
    const [userId, setUserId] = useState('');
    const [date, setDate] = useState(DateTime.now().setZone('Asia/Dubai').toFormat('yyyy-MM-dd'));
    const [hour, setHour] = useState('all');
    const [trail, setTrail] = useState([]);
    /**
     * The day's shifts, riding along with the trail.
     *
     * 2026-07-31. A trail on its own
     * opens on an anonymous dot; these give the day a start and an end that say
     * what they are.
     */
    const [shiftsToday, setShiftsToday] = useState([]);
    /** The day's marked stops (pickups/dropoffs/collects/home) for the ledger. */
    const [stopLedger, setStopLedger] = useState([]);
    /** A ledger row's point the map should pan to (row click). */
    const [focusPoint, setFocusPoint] = useState(null);
    const [focused, setFocused] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [bookings, setBookings] = useState([]);
    const [routes, setRoutes] = useState([]);

    /**
     * WHICH DAY THE JOBS BELONG TO. Live is always today; History follows the
     * day picker, so scrolling back through someone's trail shows the work
     * that existed on that day rather than today's (2026-07-30).
     */
    const activeDate = mode === 'live'
        ? DateTime.now().setZone('Asia/Dubai').toFormat('yyyy-MM-dd')
        : date;

    /* Bookings don't move, so this loads per DAY — not on the 20s live tick. */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`/api/routing/${activeDate}/booking-pins`);
                if (!cancelled) setBookings(res.data?.bookings || []);
            } catch {
                if (!cancelled) setBookings([]);
            }
        })();
        return () => { cancelled = true; };
    }, [activeDate]);

    /**
     * The day's routes, read from the ROUTES table rather than inferred from
     * the bookings: a driver assigned to a route that has no stops yet still
     * has a route, and the sidebar should say so.
     */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`/api/routing/${activeDate}/routes`);
                if (!cancelled) setRoutes(res.data?.routes || []);
            } catch {
                if (!cancelled) setRoutes([]);
            }
        })();
        return () => { cancelled = true; };
    }, [activeDate]);

    /* Everyone who has ever reported — the History picker, not just drivers. */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get('/api/routing/locations/latest?within_hours=720');
                if (cancelled) return;
                const seen = new Map();
                (res.data?.positions || []).forEach((p) => {
                    if (seen.has(p.user_id)) return;
                    seen.set(p.user_id, { id: p.user_id, name: fullName(p), role: p.role_name });
                });
                setStaff([...seen.values()].sort((a, b) =>
                    (a.role === 'driver' ? 0 : 1) - (b.role === 'driver' ? 0 : 1)
                    || a.name.localeCompare(b.name)));
            } catch { /* the drivers prop still populates the picker */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const loadLive = useCallback(async () => {
        try {
            const res = await axios.get('/api/routing/locations/latest?within_hours=12');
            setPositions(res.data?.positions || []);
            setError('');
        } catch {
            setError('Could not load positions.');
        }
    }, []);

    useEffect(() => {
        if (mode !== 'live') return undefined;
        loadLive();
        const tick = () => { if (document.visibilityState === 'visible') loadLive(); };
        const timer = setInterval(tick, 20000);
        document.addEventListener('visibilitychange', tick);
        return () => { clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
    }, [mode, loadLive]);

    const loadTrail = useCallback(async () => {
        if (!userId || !date) { setTrail([]); setShiftsToday([]); setStopLedger([]); return; }
        setLoading(true);
        try {
            const res = await axios.get(`/api/routing/locations/${userId}/trail/${date}`);
            setTrail(res.data?.points || []);
            setShiftsToday(res.data?.shifts || []);
            setError('');
        } catch {
            setError('Could not load that trail.');
            setTrail([]);
            setShiftsToday([]);
        } finally {
            setLoading(false);
        }
        // The day's marked stops ride along with the trail (2026-08-09):
        // every pickup/dropoff/collect/home leg with when it was tapped and the
        // best location we have. Failure here never hides the trail — the
        // ledger is an addition, not a dependency.
        try {
            const led = await axios.get(`/api/routing/drivers/${userId}/stops`, { params: { date } });
            setStopLedger(led.data?.stops || []);
        } catch {
            setStopLedger([]);
        }
    }, [userId, date]);

    useEffect(() => { if (mode === 'history') loadTrail(); }, [mode, loadTrail]);

    const livePins = useMemo(
        () => positions.filter((p) => Number(p.lat) && Number(p.lng)),
        [positions],
    );

    const windowed = useMemo(() => {
        if (hour === 'all') return trail;
        return trail.filter(
            (p) => DateTime.fromISO(p.recorded_at).setZone('Asia/Dubai').hour === Number(hour)
        );
    }, [trail, hour]);

    /**
     * How many rows to LIST. A whole day is hundreds of points and reads as
     * noise; two an hour reads as a shape. Zoom into an hour and you want
     * detail, so one every five minutes. Bucketed by TIME rather than "every
     * Nth point", which would clump wherever the phone reported more often.
     */
    const records = useMemo(() => {
        const bucket = hour === 'all' ? 30 : 5;
        const seen = new Map();
        windowed.forEach((p) => {
            const t = DateTime.fromISO(p.recorded_at).setZone('Asia/Dubai');
            const key = `${t.toFormat('HH')}:${Math.floor(t.minute / bucket)}`;
            if (!seen.has(key)) seen.set(key, p);
        });
        return [...seen.values()];
    }, [windowed, hour]);

    /**
     * Who can be picked. `staff` is everyone who has actually reported in the
     * last month; `drivers` is the roster. Both, because a driver who has not
     * reported yet must still be selectable — otherwise "why can I not see
     * her?" has no answer on screen.
     */
    const people = useMemo(() => {
        const byId = new Map();
        staff.forEach((p) => byId.set(String(p.id), p));
        (drivers || []).forEach((d) => {
            const id = String(d.id);
            if (byId.has(id)) return;
            byId.set(id, {
                id: d.id,
                name: [d.first_name, d.last_name].filter(Boolean).join(' ')
                    || d.name || `Staff #${d.id}`,
                role: 'driver',
            });
        });
        return [...byId.values()].sort((a, b) =>
            (a.role === 'driver' ? 0 : 1) - (b.role === 'driver' ? 0 : 1)
            || a.name.localeCompare(b.name));
    }, [staff, drivers]);

    /**
     * NOBODY PICKED → THE WHOLE DAY. Pick a driver → only the jobs on the
     * routes assigned to them (2026-07-30). Selecting somebody who is not
     * a driver, or a driver with no route that day, correctly shows nothing —
     * that IS the answer, and an empty map beside "0 of 14" says it plainly.
     */
    /**
     * Whose job is this? A booking with a cleaner belongs to whoever drives its
     * stops. One WITHOUT a cleaner has no stops at all, so the server resolves
     * the route it would fall to by area — and the office needs to see those
     * against the driver too, or picking a driver hides work that is about to
     * be hers (2026-07-30). They count towards her total for the same
     * reason.
     */
    const belongsTo = useCallback((b, driverId) => (
        (b.driver_ids || []).some((d) => String(d) === String(driverId))
        || String(b.implied_driver_id ?? '') === String(driverId)
    ), []);

    const visibleBookings = useMemo(() => {
        if (!userId) return bookings;
        return bookings.filter((b) => belongsTo(b, userId));
    }, [bookings, userId, belongsTo]);

    /**
     * SAME BUILDING, TWO JOBS. Several units in one tower share the property's
     * pinned coordinate exactly, so their pins land on top of each other and
     * only the last one drawn is readable (2026-07-30). Fan the duplicates
     * out around the shared point — a few metres on the map, enough to read,
     * near enough that nobody misreads which building they belong to.
     */
    const spreadBookings = useMemo(() => {
        const groups = new Map();
        visibleBookings.forEach((b) => {
            const key = `${Number(b.latitude).toFixed(5)},${Number(b.longitude).toFixed(5)}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(b);
        });
        const out = [];
        groups.forEach((group) => {
            if (group.length === 1) { out.push({ ...group[0], _lat: Number(group[0].latitude), _lng: Number(group[0].longitude) }); return; }
            // ~12 m at Dubai's latitude; a ring so the count doesn't matter.
            const R = 0.00011;
            group.forEach((b, i) => {
                const angle = (2 * Math.PI * i) / group.length;
                out.push({
                    ...b,
                    _lat: Number(b.latitude) + R * Math.cos(angle),
                    _lng: Number(b.longitude) + R * Math.sin(angle),
                });
            });
        });
        return out;
    }, [visibleBookings]);

    /**
     * REPORTING vs NOT (2026-07-30). Two hours is the line: a fix older
     * than that is history, not a live position, and showing it under
     * "Reporting" would have the office chasing a car that moved long ago.
     * Everyone else still appears — with their job count — because a driver
     * you cannot see is precisely the one you want to look up.
     */
    const FRESH_MS = 2 * 60 * 60 * 1000;

    /**
     * driver id -> the routes they are carrying that day. A driver can hold
     * more than one, so this keeps them all rather than picking the first and
     * quietly hiding the rest.
     */
    const routesByDriver = useMemo(() => {
        const map = new Map();
        (routes || []).forEach((r) => {
            if (r.assigned_driver_id == null) return;
            const key = String(r.assigned_driver_id);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push({ name: r.route_name, color: r.route_color });
        });
        return map;
    }, [routes]);

    /**
     * "Driver ● Marina" — the separator IS the route's colour (2026-07-30),
     * so the same colour that identifies the route's pins on the map identifies
     * its driver in the list, and the eye can join the two without reading.
     */
    const roleAndRoute = useCallback((driverId, roleLabel, isDriver) => {
        const mine = routesByDriver.get(String(driverId)) || [];
        // No route on this date means no work today — say that outright rather
        // than leaving a bare "Driver" that reads like missing data
        // (2026-07-30). Only ever said of DRIVERS: cleaners never carry a
        // route, so "off duty" would be nonsense for them.
        if (!mine.length) {
            return <>{isDriver ? `${roleLabel} · Off duty for the day` : roleLabel}</>;
        }
        return (
            <>
                {roleLabel}
                {mine.map((r, i) => (
                    <React.Fragment key={`${r.name}-${i}`}>
                        <span
                            className="dt-routedot"
                            style={r.color ? { background: r.color } : undefined}
                        />
                        {r.name}
                    </React.Fragment>
                ))}
            </>
        );
    }, [routesByDriver]);

    const liveRows = useMemo(
        () => livePins.filter(
            (p) => Date.now() - new Date(p.recorded_at).getTime() <= FRESH_MS),
        [livePins, FRESH_MS],
    );

    /**
     * Quiet drivers, ROUTED ONES FIRST (2026-07-30). Someone carrying work
     * whose phone has gone silent is the one you need to chase; someone with
     * no route today is not a problem at all. Sinking the off-duty ones to the
     * bottom keeps the top of the list actionable.
     */
    const quietRows = useMemo(() => {
        const loud = new Set(liveRows.map((p) => String(p.user_id)));
        return people
            .filter((d) => d.role === 'driver' && !loud.has(String(d.id)))
            .map((d) => ({ ...d, offDuty: !(routesByDriver.get(String(d.id)) || []).length }))
            .sort((a, b) => (a.offDuty === b.offDuty
                ? a.name.localeCompare(b.name)
                : (a.offDuty ? 1 : -1)));
    }, [people, liveRows, routesByDriver]);

    const selectedName = useMemo(() => (
        staff.find((s) => String(s.id) === String(userId))
        || (drivers || []).find((d) => String(d.id) === String(userId))
    )?.name || '', [staff, drivers, userId]);

    if (!GMAPS_KEY) {
        return (
            <div className="dt-empty">
                REACT_APP_GOOGLE_MAPS_KEY is not set, so the map cannot load.
            </div>
        );
    }

    // Frame the JOBS as well as the people, or picking a driver whose phone is
    // quiet leaves the map sitting over the whole emirate with pins off-screen.
    const bookingPoints = spreadBookings.map((b) => ({ lat: b._lat, lng: b._lng }));
    const mapPoints = [...(mode === 'live' ? livePins : windowed), ...bookingPoints];

    return (
        <div className="dt">
            <div className="dt-modes">
                <button
                    type="button"
                    className={mode === 'live' ? 'is-active' : ''}
                    onClick={() => setMode('live')}
                >
                    <span className="dt-livedot" /> Live
                </button>
                <button
                    type="button"
                    className={mode === 'history' ? 'is-active' : ''}
                    onClick={() => setMode('history')}
                >
                    History
                </button>
                {mode === 'live' && (
                    <span className="dt-modes-note">
                        {livePins.length} reporting · refreshes every 20s
                    </span>
                )}
                <span className="dt-modes-note">
                    {userId
                        ? `${visibleBookings.length} of ${bookings.length} bookings · ${selectedName || 'selected'}`
                        : `${bookings.length} bookings · ${activeDate}`}
                </span>
            </div>

            {error ? <div className="dt-error">{error}</div> : null}

            <div className="dt-split">
                <div className="dt-map">
                    <APIProvider apiKey={GMAPS_KEY}>
                        <GoogleMap
                            mapId={MAP_ID}
                            defaultCenter={DUBAI}
                            defaultZoom={11}
                            gestureHandling="greedy"
                            streetViewControl={false}
                            mapTypeControl={false}
                            fullscreenControl={false}
                        >
                            <FitToPoints points={mapPoints} paused={!!focusPoint} />
                            <PanToFocus point={focusPoint} />
                            {/* The clicked ledger row's point — a ring so it
                                reads over the trail dots and staff pins. */}
                            {focusPoint && (
                                <AdvancedMarker
                                    position={{ lat: Number(focusPoint.lat), lng: Number(focusPoint.lng) }}
                                    zIndex={500}
                                >
                                    {/* A real map pin with the row's story on a card
                                        above it — action, time, unit and place all on
                                        the MAP, so the list rows stay one line
                                        (2026-08-09). */}
                                    <div className="dt-focus-pin">
                                        <div className="dt-focus-card">
                                            <strong>{focusPoint.action || 'Point'}</strong>
                                            {/* No geocoded place line — the pin IS the
                                                location, and "Dubai" tells nobody
                                                anything (2026-08-09). */}
                                            <span>
                                                {focusPoint.time}
                                                {focusPoint.unit ? ` · ${focusPoint.unit}` : ''}
                                            </span>
                                        </div>
                                        <svg width="30" height="40" viewBox="0 0 30 40">
                                            <path
                                                d="M15 1C7.3 1 1 7.2 1 14.8 1 25 15 39 15 39s14-14 14-24.2C29 7.2 22.7 1 15 1z"
                                                fill="#111827" stroke="#ffffff" strokeWidth="2"
                                            />
                                            <circle cx="15" cy="14.5" r="5" fill="#ffffff" />
                                        </svg>
                                        <span className="dt-focus-pin__pulse" />
                                    </div>
                                </AdvancedMarker>
                            )}

                            {/* THE DAY'S WORK. Under the staff pins on purpose:
                                where a driver is standing matters more than the
                                job she is standing on. */}
                            {spreadBookings.map((b) => (
                                <AdvancedMarker
                                    key={`bk-${b.id}`}
                                    position={{ lat: b._lat, lng: b._lng }}
                                    zIndex={1}
                                    title={`${b.client_name || 'Booking'} · Unit ${b.unit_number || '—'}`
                                        + ` · ${hhmm(b.booking_start)}`
                                        + (b.address_line_1 ? ` · ${b.address_line_1}` : '')
                                        + (b.route_name ? ` · ${b.route_name}` : ' · unassigned')}
                                >
                                    {/* Anchored the same way a staff pin is — stem down
                                        to a dot on the coordinate — but in a lighter
                                        tone. The office is watching people; the jobs
                                        are the backdrop they move against. */}
                                    <span className="dt-bpin-wrap">
                                        {/* NOT a link (2026-07-30). The office is
                                            reading the day's shape, not driving to it;
                                            a stray click that threw a Google Maps tab
                                            over the board was only ever in the way. */}
                                        <span
                                            className={'dt-bpin'
                                                + (b.has_cleaner === false ? ' dt-bpin--nocleaner' : '')}
                                            title={b.has_cleaner === false
                                                ? `No cleaner scheduled yet${b.implied_route_name ? ` · would fall to ${b.implied_route_name}` : ''}`
                                                : undefined}
                                        >
                                            <span className="dt-bpin-label">
                                                {/* The swatch belongs to the TOP row only
                                                    (2026-07-30) — sitting it beside
                                                    both rows cost a column of width the
                                                    building name needs. */}
                                                <span className="dt-bpin-top">
                                                    <span
                                                        className="dt-bpin-swatch"
                                                        /* Route colour when it has one, grey
                                                           when nobody is carrying it yet — an
                                                           unassigned job should look it. */
                                                        style={b.route_color ? { background: b.route_color } : undefined}
                                                    />
                                                    {b.unit_number || '—'}
                                                    <em>
                                                        {b.has_cleaner === false
                                                            ? 'no cleaner yet'
                                                            : hhmm(b.booking_start)}
                                                    </em>
                                                </span>
                                                {b.address_line_1 && (
                                                    <span className="dt-bpin-building">{b.address_line_1}</span>
                                                )}
                                            </span>
                                        </span>
                                        <span className="dt-bpin-stem" />
                                        <span className="dt-bpin-anchor" />
                                    </span>
                                </AdvancedMarker>
                            ))}

                            {mode === 'live' && livePins.map((p) => {
                                const tone = toneFor(p.role_name);
                                return (
                                    <AdvancedMarker
                                        key={p.user_id}
                                        position={{ lat: Number(p.lat), lng: Number(p.lng) }}
                                        onClick={() => setFocused(p.user_id)}
                                        /**
                                         * A PERSON IS NEVER HIDDEN BEHIND A BUILDING
                                         * (2026-08-01). The booking pins carry
                                         * zIndex 1 and these carried none, so Google
                                         * was free to paint a job on top of the driver
                                         * standing at it — and at a shared address it
                                         * did. Where somebody IS outranks where the
                                         * work is, and the one being followed outranks
                                         * everyone.
                                         */
                                        zIndex={focused === p.user_id ? 400 : 300}
                                        title={`${fullName(p)} · ${sinceLabel(p.recorded_at)}`}
                                    >
                                        {/* A LABELLED pin. The office should not have to hover
                                            a dot to find out who it is, or which car. */}
                                        <div
                                            className={'dt-pin-wrap'
                                                + (focused === p.user_id ? ' is-focused' : '')
                                                + ` dt-pin--${freshness(p.recorded_at)}`}
                                        >
                                          <div className="dt-pin">
                                            <span
                                                className="dt-pin-icon"
                                                style={{ background: tone.bg }}
                                            >
                                                {tone.glyph}
                                                {/* WHICH WAY SHE IS POINTING (the owner
                                                    2026-07-27). course_deg is the
                                                    phone's own heading when it sends
                                                    one, else the bearing from the
                                                    previous fix. The server returns
                                                    null when the last two fixes are
                                                    within 15m — a parked car jitters
                                                    on GPS noise, and an arrow spun by
                                                    noise is worse than no arrow. */}
                                                
                                            </span>
                                            <span className="dt-pin-text">
                                                <strong>{p.first_name || fullName(p)}</strong>
                                                <em>{p.vehicle || tone.label}</em>
                                            </span>
                                          </div>
                                          {/* THE ANCHOR (2026-07-27). The label
                                              floats above the map; without a stem
                                              running down to a dot there is no way to
                                              tell WHICH point on the road it belongs
                                              to. The tip of this dot is the actual
                                              coordinate — AdvancedMarker pins the
                                              bottom-centre of this element to it. */}
                                          <span className="dt-pin-stem" />
                                          <span className="dt-pin-dot" />
                                        </div>
                                    </AdvancedMarker>
                                );
                            })}

                            {mode === 'history' && windowed.length > 0 && (
                                <>
                                    <Trail points={windowed} />
                                    <AdvancedMarker
                                        position={{
                                            lat: Number(windowed[0].lat),
                                            lng: Number(windowed[0].lng),
                                        }}
                                        zIndex={300}
                                        title={`Started ${hhmm(windowed[0].recorded_at)}`}
                                    >
                                        <span className="dt-cap dt-cap--a">A</span>
                                    </AdvancedMarker>
                                    <AdvancedMarker
                                        position={{
                                            lat: Number(windowed[windowed.length - 1].lat),
                                            lng: Number(windowed[windowed.length - 1].lng),
                                        }}
                                        zIndex={300}
                                        title={`Last seen ${hhmm(windowed[windowed.length - 1].recorded_at)}`}
                                    >
                                        <span className="dt-cap dt-cap--b">B</span>
                                    </AdvancedMarker>
                                </>
                            )}
                        </GoogleMap>
                    </APIProvider>
                </div>

                <aside className="dt-side">
                    {mode === 'history' ? (
                        <>
                            <div className="dt-filters">
                                {/* A PICKER, NOT A DROPDOWN (2026-07-27). With a
                                    handful of staff, opening a select to pick one and
                                    then opening it again to compare is two clicks per
                                    look. These are one click each, and the whole team
                                    is readable at a glance. */}
                                <div className="dt-field">
                                    <span>Who</span>
                                    <div className="dt-who">
                                        {people.map((d) => {
                                            const tone = toneFor(d.role);
                                            const on = String(d.id) === String(userId);
                                            return (
                                                <button
                                                    key={d.id}
                                                    type="button"
                                                    className={`dt-who-btn${on ? ' is-on' : ''}`}
                                                    onClick={() => setUserId(on ? '' : String(d.id))}
                                                    title={d.role || ''}
                                                >
                                                    <span className="av" style={{ background: tone.bg }}>
                                                        {initials(d.name)}
                                                    </span>
                                                    <span className="nm">
                                                        <strong>{d.name}</strong>
                                                        <em>{tone.label}</em>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                        {!people.length && (
                                            <p className="dt-who-empty">
                                                Nobody has reported a position yet.
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="dt-field-row">
                                    <label className="dt-field">
                                        <span>Day</span>
                                        <input
                                            type="date"
                                            value={date}
                                            onChange={(e) => setDate(e.target.value)}
                                        />
                                    </label>
                                    <label className="dt-field">
                                        <span>Hours</span>
                                        <select value={hour} onChange={(e) => setHour(e.target.value)}>
                                            <option value="all">Whole day</option>
                                            {Array.from({ length: 24 }, (_, h) => (
                                                <option key={h} value={String(h).padStart(2, '0')}>
                                                    {String(h).padStart(2, '0')}:00
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </div>

                            {trail.length > 0 && (
                                <div className="dt-stats">
                                    <div><strong>{trail.length}</strong><span>points</span></div>
                                    <div><strong>{hhmm(trail[0].recorded_at)}</strong><span>first</span></div>
                                    <div>
                                        <strong>{hhmm(trail[trail.length - 1].recorded_at)}</strong>
                                        <span>last</span>
                                    </div>
                                </div>
                            )}

                            <div className="dt-list-head">
                                {loading ? 'Loading…'
                                    : !userId ? 'Choose someone to see their day'
                                        : records.length
                                            ? `${records.length} shown · every ${hour === 'all' ? '30' : '5'} min`
                                            : 'Nothing recorded in this window'}
                            </div>

                            <ol className="dt-records">
                                {/* THE DAY OPENS WITH A CLOCK-ON. Always first,
                                    whatever the breadcrumbs say — a day that
                                    begins on an anonymous dot tells you nothing
                                    about when work actually started. */}
                                {shiftsToday.length > 0 && (
                                    <li
                                        className={`dt-rec dt-rec--on${shiftsToday[0].on.lat ? ' dt-rec--clickable' : ''}`}
                                        onClick={shiftsToday[0].on.lat
                                            ? () => setFocusPoint({
                                                lat: shiftsToday[0].on.lat,
                                                lng: shiftsToday[0].on.lng,
                                                action: 'Clock on',
                                                time: hhmm(shiftsToday[0].on.at),
                                            })
                                            : undefined}
                                        title={shiftsToday[0].on.lat ? 'Show where the day started' : undefined}
                                    >
                                        <span className="t">{hhmm(shiftsToday[0].on.at)}</span>
                                        <span className="m">
                                            <strong>Clock on</strong>
                                            <span className="where">
                                                <PlaceName
                                                    lat={shiftsToday[0].on.lat}
                                                    lng={shiftsToday[0].on.lng}
                                                />
                                                {shiftsToday[0].on._approx ? ' (nearby)' : ''}
                                            </span>
                                            <span className="meta">
                                                {shiftsToday[0].vehicle}
                                                {shiftsToday[0].start_odometer != null
                                                    ? ` · ${Number(shiftsToday[0].start_odometer).toLocaleString()} km`
                                                    : ''}
                                            </span>
                                        </span>
                                        <span className="g" />
                                    </li>
                                )}

                                {/* Breadcrumbs and MARKED STOPS in one timeline
                                    (2026-08-09): every pickup / dropoff /
                                    collect / home-drop the driver tapped,
                                    in the moment it happened between the GPS
                                    fixes. `stamped` coords come from the tap
                                    itself (app 1.0.1+); otherwise the nearest
                                    trail fix within ±5 min, labelled ~approx —
                                    a batch-tap gets the drive location, and the
                                    admin deserves to know which they're seeing. */}
                                {[...records.map((p) => ({ kind: 'fix', at: p.recorded_at, p })),
                                  ...stopLedger
                                      .filter((s) => s.completed_at)
                                      .map((s) => ({ kind: 'stop', at: s.completed_at, s })),
                                ].sort((a, b) => new Date(a.at) - new Date(b.at)).map((item) => (
                                    item.kind === 'fix' ? (
                                        <li
                                            key={`f${item.p.recorded_at}`}
                                            className="dt-rec--clickable"
                                            onClick={() => setFocusPoint({
                                                lat: item.p.lat,
                                                lng: item.p.lng,
                                                action: item.p.is_moving ? 'Moving' : 'Stopped',
                                                time: hhmm(item.p.recorded_at),
                                            })}
                                            title="Show this point on the map"
                                        >
                                            <span className="t">{hhmm(item.p.recorded_at)}</span>
                                            <span className="m">
                                                <i className={item.p.is_moving ? 'moving' : 'stopped'} />
                                                {item.p.is_moving ? 'moving' : 'stopped'}
                                                {item.p.speed_mps != null && Number(item.p.speed_mps) > 0
                                                    ? ` · ${Math.round(Number(item.p.speed_mps) * 3.6)} km/h`
                                                    : ''}
                                            </span>
                                            <span className="g" />
                                        </li>
                                    ) : (
                                        <li
                                            key={`s${item.s.id}`}
                                            className={`dt-rec--stop${item.s.lat != null ? ' dt-rec--clickable' : ''}`}
                                            onClick={item.s.lat != null
                                                ? () => setFocusPoint({
                                                    lat: item.s.lat,
                                                    lng: item.s.lng,
                                                    action: stopLabel(item.s),
                                                    time: hhmm(item.s.completed_at),
                                                    unit: item.s.unit,
                                                    source: item.s.loc_source,
                                                })
                                                : undefined}
                                            title={`${stopLabel(item.s)}${item.s.unit ? ` · ${item.s.unit}` : ''} · ${hhmm(item.s.completed_at)}`}
                                        >
                                            <span className="t">{hhmm(item.s.completed_at)}</span>
                                            <span className="m">
                                                {/* Same dot as the moving/stopped breadcrumbs, colour-
                                                    coded by leg — no card, no side border, one smooth
                                                    list (2026-08-09). */}
                                                <i className={`stopdot--${item.s.status === 'skipped' ? 'skipped' : item.s.stop_type}`} />
                                                <span className="act">
                                                    {stopLabel(item.s)}
                                                    {item.s.unit && (item.s.stop_type === 'dropoff' || item.s.stop_type === 'collect')
                                                        ? ` · ${item.s.unit}` : ''}
                                                    {item.s.note ? ` · “${item.s.note}”` : ''}
                                                </span>
                                            </span>
                                            <span className="g" />
                                        </li>
                                    )
                                ))}

                                {/* AND CLOSES WITH A CLOCK-OFF — the last shift
                                    of the day, and only once it has actually
                                    been closed. An open shift simply shows
                                    nothing yet (2026-08-09). */}
                                {shiftsToday.length > 0 && (() => {
                                    const last = shiftsToday[shiftsToday.length - 1];
                                    if (!last.off) return null;
                                    const km = last.end_odometer != null && last.start_odometer != null
                                        ? Number(last.end_odometer) - Number(shiftsToday[0].start_odometer)
                                        : null;
                                    return (
                                        <li
                                            className={`dt-rec dt-rec--off${last.off.lat ? ' dt-rec--clickable' : ''}`}
                                            onClick={last.off.lat
                                                ? () => setFocusPoint({
                                                    lat: last.off.lat,
                                                    lng: last.off.lng,
                                                    action: 'Clock out',
                                                    time: hhmm(last.off.at),
                                                })
                                                : undefined}
                                            title={last.off.lat ? 'Show where the day ended' : undefined}
                                        >
                                            <span className="t">{hhmm(last.off.at)}</span>
                                            <span className="m">
                                                <strong>Clock out</strong>
                                                <span className="where">
                                                    <PlaceName lat={last.off.lat} lng={last.off.lng} />
                                                    {last.off._approx ? ' (nearby)' : ''}
                                                </span>
                                                <span className="meta">
                                                    {last.end_odometer != null
                                                        ? `${Number(last.end_odometer).toLocaleString()} km`
                                                        : ''}
                                                    {km != null ? ` · ${km.toLocaleString()} km driven` : ''}
                                                    {last.corrected ? ' · corrected' : ''}
                                                </span>
                                            </span>
                                            <span className="g" />
                                        </li>
                                    );
                                })()}
                            </ol>

                            {selectedName ? (
                                <div className="dt-side-foot">{selectedName} · {date}</div>
                            ) : null}
                        </>
                    ) : (
                        <>
                            {/* REPORTING — a live position to click. */}
                            <div className="dt-list-head">
                                {liveRows.length ? 'Reporting' : 'Nobody reporting'}
                            </div>
                            <ul className="dt-people">
                                {liveRows.map((p) => {
                                    const tone = toneFor(p.role_name);
                                    const on = String(p.user_id) === String(userId);
                                    /**
                                     * WHY a trail is dark, in the phone's own words
                                     * (2026-08-09). `tracking_health` is what the
                                     * app last reported about its settings; "dark" is
                                     * ours — clocked on yet silent for 15+ minutes.
                                     * Android-only complaints: an iPhone reporting
                                     * only in the foreground is Apple's rule, not a
                                     * setting anyone can fix.
                                     */
                                    const h = p.tracking_health;
                                    const warns = [];
                                    if (p.on_shift && p.stale_minutes >= 15) warns.push(`tracking dark ${p.stale_minutes} min`);
                                    if (h && h.platform === 'android') {
                                        if (h.bg_permission !== 'background') warns.push('location: while app open only');
                                        if (h.battery_optimized === true) warns.push('battery: restricted');
                                        if (h.notifications_granted === false) warns.push('notifications off');
                                    }
                                    return (
                                        <li
                                            key={p.user_id}
                                            className={(focused === p.user_id ? 'is-focused' : '')
                                                + (on ? ' is-picked' : '')}
                                            /* Clicking a person filters the day's jobs to
                                               theirs; clicking again clears it. */
                                            onClick={() => {
                                                setFocused(p.user_id);
                                                setUserId(on ? '' : String(p.user_id));
                                            }}
                                        >
                                            <span className="av" style={{ background: tone.bg }}>
                                                {initials(fullName(p))}
                                            </span>
                                            <span className="who">
                                                <strong>{fullName(p)}</strong>
                                                <em>
                                                    {roleAndRoute(p.user_id, tone.label, p.role_name === 'driver')}
                                                    {p.vehicle ? ` · ${p.vehicle}` : ''}
                                                </em>
                                                {warns.length > 0 && (
                                                    <em className="dt-health-warn" title="Reported by the driver's own phone">
                                                        ⚠ {warns.join(' · ')}
                                                    </em>
                                                )}
                                            </span>
                                            <span className={`age age--${freshness(p.recorded_at)}`}>
                                                {sinceLabel(p.recorded_at)}
                                                {p.battery_pct != null ? ` · ${p.battery_pct}%` : ''}
                                            </span>
                                        </li>
                                    );
                                })}
                                {!liveRows.length && (
                                    <li className="is-empty">
                                        Nobody has reported recently.
                                    </li>
                                )}
                            </ul>

                            {/* NOT REPORTING — still selectable (2026-07-30). A
                                driver whose phone is quiet still has a route, and
                                "where should she be" is exactly the question you ask
                                when you cannot see her. Kept in its own list so a
                                silent phone is never mistaken for a live one. */}
                            {quietRows.length > 0 && (
                                <>
                                    <div className="dt-list-head dt-list-head--quiet">
                                        Not reporting · {quietRows.length}
                                    </div>
                                    <ul className="dt-people dt-people--quiet">
                                        {quietRows.map((d) => {
                                            const tone = toneFor(d.role);
                                            const on = String(d.id) === String(userId);
                                            const jobs = bookings.filter((b) => belongsTo(b, d.id)).length;
                                            return (
                                                <li
                                                    key={`q-${d.id}`}
                                                    className={(on ? 'is-picked' : '')
                                                        + (d.offDuty ? ' is-offduty' : '')}
                                                    onClick={() => setUserId(on ? '' : String(d.id))}
                                                >
                                                    <span className="av" style={{ background: tone.bg }}>
                                                        {initials(d.name)}
                                                    </span>
                                                    <span className="who">
                                                        <strong>{d.name}</strong>
                                                        <em>{roleAndRoute(d.id, tone.label, true)}</em>
                                                    </span>
                                                    <span className="age age--stale">
                                                        {jobs ? `${jobs} job${jobs === 1 ? '' : 's'}` : ''}
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </>
                            )}
                        </>
                    )}
                </aside>
            </div>
        </div>
    );
}
